import React from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useTheme } from "../../context/ThemeContext";
import {
  SearchWrapper,
  SearchInput,
  SearchIcon,
  SearchClearButton,
} from "./Style";
import { CustomInputProps } from "./types";

/**
 * Common Search Input Component
 * A reusable search input component with icon and clear button
 *
 * @param value - Input value
 * @param onChange - Callback when value changes (receives string value)
 * @param placeholder - Placeholder text (default: "Search...")
 * @param allowClear - Allow clearing input (default: true)
 * @param disabled - Disable the input
 * @param width - Width of the input (default: "251px")
 * @param ...rest - Other input props (extends React.InputHTMLAttributes)
 * 
 * @example
 * ```tsx
 * <CustomInput
 *   value={searchQuery}
 *   onChange={(value) => setSearchQuery(value)}
 *   placeholder="Search users..."
 *   width="300px"
 *   allowClear
 * />
 * ```
 */
const CustomInput: React.FC<CustomInputProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  allowClear = true,
  disabled = false,
  width,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? "dark" : "light";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }
  };

  return (
    <SearchWrapper width={width}>
      <SearchInput
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        theme={theme}
        {...rest}
      />
      <SearchIcon theme={theme} />
      {value && allowClear && (
        <SearchClearButton onClick={handleClear} type="button" theme={theme}>
          <CloseOutlined />
        </SearchClearButton>
      )}
    </SearchWrapper>
  );
};

export default CustomInput;


